import React from 'react';
import { v2Color } from '@theme/v2';

/**
 * Inline SVG glyphs extracted verbatim from the REGA Ad License HTML prototypes
 * (Profolio dashboard + post-listing), kept as React components so the feature
 * renders pixel-identical to the design. `color` maps to currentColor.
 */

const svgBase = { display: 'block' };

export const InfoCircleIcon = ({ size = 14, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ ...svgBase, color }}>
    <circle cx="12" cy="12" r="8.6" stroke="currentColor" strokeWidth="1.7" />
    <path d="M12 11v5.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="12" cy="8.1" r="1" fill="currentColor" />
  </svg>
);

export const ChevronRightIcon = ({ size = 14, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ ...svgBase, color }}>
    <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ChevronLeftIcon = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ ...svgBase, color }}>
    <path d="m15 6-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const TickIcon = ({ size = 15, color = 'currentColor', strokeWidth = 2.6 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ ...svgBase, color }}>
    <path
      d="m5 12.5 4.5 4.5L19 7.5"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const LockIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={svgBase}>
    <rect x="4.5" y="10.5" width="15" height="10.5" rx="2.5" fill="#f0a742" />
    <path d="M8 10.5V7.75a4 4 0 0 1 8 0v2.75" stroke="#f0a742" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// Clear-state success check; the path draws itself via the adlTickDraw keyframes.
export const AnimatedCheckIcon = ({ size = 15, color = v2Color('#006169') }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ ...svgBase, position: 'relative' }}>
    <path
      d="m5 12.5 4.5 4.5L19 7.5"
      stroke={color}
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeDasharray="24"
      style={{ animation: 'adlTickDraw 0.75s ease-out 0.15s both' }}
    />
  </svg>
);

export const SearchIcon = ({ size = 17, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ ...svgBase, color }}>
    <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.8" />
    <path d="m16 16 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

export const PinIcon = ({ size = 12, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 384 512" fill="currentColor" style={{ ...svgBase, color, flex: '0 0 auto' }}>
    <path d="M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0zM192 272c44.183 0 80-35.817 80-80s-35.817-80-80-80-80 35.817-80 80 35.817 80 80 80z" />
  </svg>
);

export const AreaIcon = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 512 512" fill="currentColor" style={{ ...svgBase, color, flex: '0 0 auto' }}>
    <path d="M12.41 148.02l232.94 105.67c6.8 3.09 14.49 3.09 21.29 0l232.94-105.67c16.55-7.51 16.55-32.52 0-40.03L266.65 2.31a25.607 25.607 0 00-21.29 0L12.41 107.99c-16.55 7.51-16.55 32.52 0 40.03zm487.18 88.28l-58.09-26.33-161.64 73.27c-7.56 3.43-15.59 5.17-23.86 5.17s-16.29-1.74-23.86-5.17L70.51 209.97l-58.1 26.33c-16.55 7.51-16.55 32.52 0 40.03l232.94 105.68c6.8 3.09 14.49 3.09 21.29 0L499.59 276.3c16.55-7.5 16.55-32.5 0-40zm0 127.8l-57.87-26.23-161.86 73.37c-7.56 3.43-15.59 5.17-23.86 5.17s-16.29-1.74-23.86-5.17L70.29 337.86 12.41 364.1c-16.55 7.51-16.55 32.52 0 40.03l232.94 105.67c6.8 3.09 14.49 3.09 21.29 0l232.94-105.67c16.56-7.51 16.56-32.52.01-40.03z" />
  </svg>
);

export const CalendarIcon = ({ size = 12, color = 'currentColor' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ ...svgBase, color, flex: '0 0 auto' }}
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

export const CloseIcon = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 512 512" fill="currentColor" style={{ ...svgBase, color }}>
    <path d="M405 136.798L375.202 107 256 226.202 136.798 107 107 136.798 226.202 256 107 375.202 136.798 405 256 285.798 375.202 405 405 375.202 285.798 256z" />
  </svg>
);

export const AssistListIcon = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" style={{ ...svgBase, color }}>
    <path
      d="M7.50008 5H16.6667M7.50008 10H16.6667M7.50008 15H16.6667M4.16675 5V5.00833M4.16675 10V10.0083M4.16675 15V15.0083"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

export const SealCheckIcon = ({ size = 18, color = v2Color('#006169') }) => (
  <svg width={size} height={size} viewBox="0 0 256 256" fill="currentColor" style={{ ...svgBase, color }}>
    <path d="M225.86,102.82c-3.77-3.94-7.67-8-9.14-11.57-1.36-3.27-1.44-8.69-1.52-13.94-.15-9.76-.31-20.82-8-28.51s-18.75-7.85-28.51-8c-5.25-.08-10.67-.16-13.94-1.52-3.56-1.47-7.63-5.37-11.57-9.14C146.28,23.51,138.44,16,128,16s-18.27,7.51-25.18,14.14c-3.94,3.77-8,7.67-11.57,9.14C88,40.64,82.56,40.72,77.31,40.8c-9.76.15-20.82.31-28.51,8S41,67.55,40.8,77.31c-.08,5.25-.16,10.67-1.52,13.94-1.47,3.56-5.37,7.63-9.14,11.57C23.51,109.72,16,117.56,16,128s7.51,18.27,14.14,25.18c3.77,3.94,7.67,8,9.14,11.57,1.36,3.27,1.44,8.69,1.52,13.94.15,9.76.31,20.82,8,28.51s18.75,7.85,28.51,8c5.25.08,10.67.16,13.94,1.52,3.56,1.47,7.63,5.37,11.57,9.14C109.72,232.49,117.56,240,128,240s18.27-7.51,25.18-14.14c3.94-3.77,8-7.67,11.57-9.14,3.27-1.36,8.69-1.44,13.94-1.52,9.76-.15,20.82-.31,28.51-8s7.85-18.75,8-28.51c.08-5.25.16-10.67,1.52-13.94,1.47-3.56,5.37-7.63,9.14-11.57C232.49,146.28,240,138.44,240,128S232.49,109.73,225.86,102.82Zm-52.2,6.84-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35a8,8,0,0,1,11.32,11.32Z" />
  </svg>
);

export const LicenseBannerIcon = ({ size = 44 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 52 52" fill="none" style={svgBase}>
    <path
      stroke="url(#aLicenseBannerIcon)"
      strokeWidth="2"
      d="M11 8.5a2.5 2.5 0 0 1 2.5-2.5h21a2.5 2.5 0 0 1 2.5 2.5v31a2.5 2.5 0 0 1-2.5 2.5h-21A2.5 2.5 0 0 1 11 39.5z"
    />
    <path fill="url(#bLicenseBannerIcon)" d="M16.5 13.5h15v2.75h-15z" />
    <path fill="url(#cLicenseBannerIcon)" d="M16.5 20h15v2.75h-15z" />
    <path fill="url(#dLicenseBannerIcon)" d="M16.5 26.5h9v2.75h-9z" />
    <path fill="url(#eLicenseBannerIcon)" d="m30.9 41.2 3.4-1.1 1.5 3.2 2.6-8.6-7.5 2.3z" />
    <path fill="url(#fLicenseBannerIcon)" d="m43.6 41.2-3.4-1.1-1.5 3.2-2.6-8.6 7.5 2.3z" />
    <path
      fill="url(#gLicenseBannerIcon)"
      d="M37.25 22.5a9.25 9.25 0 1 1 0 18.5 9.25 9.25 0 0 1 0-18.5m0 3.4a5.85 5.85 0 1 0 0 11.7 5.85 5.85 0 0 0 0-11.7"
    />
    <path fill="url(#hLicenseBannerIcon)" d="m36.28 33.53-1.5-1.5-1.6 1.6 3.1 3.1 5.3-5.3-1.6-1.6z" />
    <defs>
      {['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'].map((id) => (
        <linearGradient key={id} id={`${id}LicenseBannerIcon`} gradientUnits="userSpaceOnUse">
          <stop stopColor="#7ECCA5" />
          <stop offset="1" stopColor={v2Color('#005960')} />
        </linearGradient>
      ))}
    </defs>
  </svg>
);
